/**
 * analytics.ts
 *
 * Single entry point for all analytics tracking.
 * - Writes to MongoDB via POST /api/analytics/event
 * - Also fires window.gtag() if GA4 is loaded
 * - Auto-attaches UTM params from the current URL
 * - Generates/retrieves a per-tab session ID from sessionStorage
 * - Never throws — analytics must never break the UI
 * - Queues failed requests and retries once after 5 seconds
 */

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

export interface AnalyticsPayload {
  bandId?: string;
  bandName?: string;
  musicianName?: string;
  channel?: string;
  viewType?: string;
  referrer?: string;
  userAgent?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  sessionId?: string;
}

// ── Session ID ─────────────────────────────────────────────────────
function getSessionId(): string {
  try {
    const key = 'imc_session_id';
    let id = sessionStorage.getItem(key);
    if (!id) {
      id = crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem(key, id);
    }
    return id;
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

// ── UTM params from current URL ────────────────────────────────────
function getUtmParams(): Pick<AnalyticsPayload, 'utmSource' | 'utmMedium' | 'utmCampaign'> {
  try {
    const params = new URLSearchParams(window.location.search);
    return {
      utmSource:   params.get('utm_source')   || undefined,
      utmMedium:   params.get('utm_medium')   || undefined,
      utmCampaign: params.get('utm_campaign') || undefined,
    };
  } catch {
    return {};
  }
}

// ── Failed event queue with single retry ──────────────────────────
const retryQueue: Array<{ event: string; payload: AnalyticsPayload }> = [];
let retryScheduled = false;

function scheduleRetry() {
  if (retryScheduled) return;
  retryScheduled = true;
  setTimeout(async () => {
    retryScheduled = false;
    const items = retryQueue.splice(0);
    for (const item of items) {
      await sendToMongo(item.event, item.payload, false);
    }
  }, 5000);
}

// ── Send to MongoDB ────────────────────────────────────────────────
async function sendToMongo(
  event: string,
  payload: AnalyticsPayload,
  shouldRetry = true,
): Promise<void> {
  const apiBase = process.env.REACT_APP_API_URL || '';
  try {
    await fetch(`${apiBase}/api/analytics/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event, ...payload }),
      // keepalive so the request survives page unloads
      keepalive: true,
    });
  } catch {
    if (shouldRetry) {
      retryQueue.push({ event, payload });
      scheduleRetry();
    }
  }
}

// ── Main export ────────────────────────────────────────────────────
export const trackEvent = (
  event: string,
  data?: Partial<AnalyticsPayload>,
): void => {
  try {
    const utms = getUtmParams();
    const payload: AnalyticsPayload = {
      sessionId:  getSessionId(),
      referrer:   document.referrer || undefined,
      userAgent:  navigator.userAgent || undefined,
      ...utms,
      ...data,
    };

    // 1. Write to MongoDB (fire-and-forget)
    sendToMongo(event, payload).catch(() => {});

    // 2. Mirror to GA4 if loaded
    if (typeof window.gtag === 'function') {
      window.gtag('event', event, {
        band_name:     payload.bandName,
        musician_name: payload.musicianName,
        channel:       payload.channel,
        view_type:     payload.viewType,
        utm_source:    payload.utmSource,
        utm_medium:    payload.utmMedium,
        utm_campaign:  payload.utmCampaign,
      });
    }
  } catch {
    // Swallow all errors — analytics must never break the UI
  }
};

// ── UTM URL builder (used by ShareButton) ─────────────────────────
export function appendUtm(
  url: string,
  source: string,
  medium = 'share',
  campaign = 'band_share',
): string {
  try {
    const u = new URL(url, window.location.origin);
    u.searchParams.set('utm_source', source);
    u.searchParams.set('utm_medium', medium);
    u.searchParams.set('utm_campaign', campaign);
    return u.toString();
  } catch {
    return url;
  }
}
